import React, { useRef, useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styles from "./PosRoleEdit.module.css";
import { post, get, put } from "../../Common/api";
import Alert, {
  success as successNotificationType,
  error as errorNotificationType,
  getNotificationForError,
} from "../Alert";
import { defaultErrorMessage } from "../../Common/UserException";
import {
  setCurrentPageNotification,
  setNextPageNotification,
  selectNotification,
} from "../../redux/notificationSlice";
import CancelPosRoleDialog from "./CancelPosRoleDialog";

export default function PosRoleEdit() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const nameInputRef = useRef(null);
  const isNewRole = !id;
  const [isSaveEnabled, setIsSaveEnabled] = useState(true);
  const [hasNameValidationError, setHasNameValidationError] = useState(false);
  const [isPosRoleCancelDialogVisible, setIsPosRoleCancelDialogVisible] =
    React.useState(false);

  useEffect(() => {
    let mounted = true;
    if (id) {
      get(`/posroles/${id}`)
        .catch((error) => {
          dispatch(setNextPageNotification(getNotificationForError(error)));
          redirectToPosRoleList();
        })
        .then((savedRole) => {
          if (savedRole) {
            if (mounted) {
              setInitialRole(savedRole);
              setRole(savedRole);
            }
          }
        });
    } else {
      get(`/posroles/template`)
        .catch((error) => {
          console.error(error);
          dispatch(setNextPageNotification(getNotificationForError(error)));
          redirectToPosRoleList();
        })
        .then((template) => {
          if (mounted && template) {
            template = { ...template, name: "Untitled" };
            setInitialRole(template);
            setRole(template);
          }
        });
    }
    return () => mounted = false;
  }, [id, dispatch]);

  const [initialRole, setInitialRole] = React.useState({});
  const [role, setRole] = React.useState(initialRole);

  const setName = (name) => {
    const truncatedName = name.length > 15 ? name.substring(0, 15) : name;
    const newRole = { ...role, name: truncatedName };
    setRole(newRole);
  };

  const doSaveValidation = () => {
    let isValid = true;
    if ((role.name || "").length === 0) {
      window.scrollTo(0, 0);
      nameInputRef.current.focus();
      dispatch(
        setCurrentPageNotification({
          message: "You must specify a POS role name.",
          type: errorNotificationType,
        })
      );
      setHasNameValidationError(true);
      isValid = false;
    }
    return isValid;
  };

  const saveRole = async () => {
    let valid = doSaveValidation();
    if (!valid) {
      return;
    }
    const getModulePermissions = (modulePermissions) => {
      const result = modulePermissions.map((b) => {
        return b;
      });
      return result;
    };

    // TODO: get role dto
    const roleDto = {
      name: role.name,
      modulePermissions: getModulePermissions(role.modulePermissions),
    };
    try {
      setIsSaveEnabled(false);
      if (isNewRole) {
        await post("/posroles", roleDto);
      } else {
        await put(`/posroles/${id}`, roleDto);
      }
      dispatch(
        setNextPageNotification({
          type: successNotificationType,
          message: `POS role "${role.name}" saved.`,
        })
      );

      history.push("/posroles");
    } catch (error) {
      let message = defaultErrorMessage;
      if (error.type === "UserException") {
        message = error.message;
        setHasNameValidationError(true);
      }
      if (error.type == "NotFoundException") {
        message = error.message;
      }
      dispatch(
        setCurrentPageNotification({
          type: errorNotificationType,
          message: message,
        })
      );
      window.scrollTo(0, 0);
      nameInputRef.current.focus();
    }
    setIsSaveEnabled(true);
  };

  const cancelRoleChanges = () => {
    if (initialRole !== role) {
      setIsPosRoleCancelDialogVisible(true);
      return;
    }
    redirectToPosRoleList();
  };

  const redirectToPosRoleList = () => {
    setIsPosRoleCancelDialogVisible(false);
    history.push("/posroles");
  };

  const dummyOnChange = (e) => {
    /* Dummy on change to prevent warning about
       read-only check box. Event will bubble up to containing
       element */
  }

  const modulePermissions = (role || {}).modulePermissions || [];
  const pos = modulePermissions.filter((mp) => mp.module == 25)[0];
  const orders = modulePermissions.filter((mp) => mp.module == 2)[0];
  // Use -1 as key for <tr> so it doesn't conflict with the
  // zero-index keys of the main list of modules
  const posModuleRow = !pos ? null : (
    <tr key={-1}> 
      <td className={styles["tg-0pky"]}>{pos.moduleName}</td>
      <td
        className={styles["tg-c3ow"]}
        role="button"
        aria-label={
          (pos.canView ? "Revoke" : "Grant") +
          " permission to view " +
          pos.moduleName
        }
        onClick={() => toggleCanView(pos)}
      >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
        <input type="checkbox" checked={!!pos.canView} onChange={dummyOnChange} />
      </td>
      <td className={styles["tg-c3ow"]} />
      <td
        className={styles["tg-c3ow"]}
        role="button"
        aria-label={
          (pos.canEdit ? "Revoke" : "Grant") +
          " permission to update " +
          pos.moduleName
        }
        onClick={() => toggleCanEdit(pos)}
      >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
        <input type="checkbox" checked={!!pos.canEdit} onChange={dummyOnChange} />
      </td>
      <td
        className={styles["tg-c3ow"]}
        role="button"
        aria-label={
          (pos.canDelete ? "Revoke" : "Grant") +
          " permission to delete " +
          pos.moduleName
        }
        onClick={() => toggleCanDelete(pos)}
      >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
        <input type="checkbox" checked={!!pos.canDelete} onChange={dummyOnChange} />
      </td>
      <td
        className={styles["tg-c3ow"]}
        role="button"
        aria-label={
          (orders.canView ? "Revoke" : "Grant") +
          " permission to search customers "
        }
        onClick={() => toggleCanView(orders)}
      >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
        <input type="checkbox" checked={!!orders.canView} onChange={dummyOnChange} />
      </td>
      <td
        className={styles["tg-c3ow"]}
        role="button"
        aria-label={
          (orders.canInsert ? "Revoke" : "Grant") +
          " permission to override low balance warning"
        }
        onClick={() => toggleCanInsert(orders)}
      >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
        <input type="checkbox" checked={!!orders.canInsert} onChange={dummyOnChange} />
      </td>
      <td
        className={styles["tg-c3ow"]}
        role="button"
        aria-label={
          (orders.canEdit ? "Revoke" : "Grant") + " permission to void orders"
        }
        onClick={() => toggleCanEdit(orders)}
      >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
        <input type="checkbox" checked={!!orders.canEdit} onChange={dummyOnChange} />
      </td>
    </tr>
  );
  const sortedModules = modulePermissions.filter(
    (mp) => mp.moduleName !== "Unknown"
  );
  sortedModules.sort((a, b) => a.sortOrder - b.sortOrder);
  const moduleList = sortedModules.map((mp, idx) =>
    mp.module == 2 ? null : mp.module == 25 ? (
      posModuleRow
    ) : (
      <tr key={idx}>
        <td className={styles["tg-0pky"]}>{mp.moduleName}</td>
        <td
          className={styles["tg-c3ow"]}
          role="button"
          aria-label={
            (mp.canView ? "Revoke" : "Grant") +
            " permission to view " +
            mp.moduleName
          }
          onClick={() => toggleCanView(mp)}
        >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
          <input type="checkbox" checked={!!mp.canView} onChange={dummyOnChange} />
        </td>
        <td
          className={styles["tg-c3ow"]}
          role="button"
          aria-label={
            (mp.canInsert ? "Revoke" : "Grant") +
            " permission to create " +
            mp.moduleName
          }
          onClick={() => toggleCanInsert(mp)}
        >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
          <input type="checkbox" checked={!!mp.canInsert} onChange={dummyOnChange} />
        </td>
        <td
          className={styles["tg-c3ow"]}
          role="button"
          aria-label={
            (mp.canEdit ? "Revoke" : "Grant") +
            " permission to update " +
            mp.moduleName
          }
          onClick={() => toggleCanEdit(mp)}
        >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
          <input type="checkbox" checked={!!mp.canEdit} onChange={dummyOnChange} />
        </td>
        <td
          className={styles["tg-c3ow"]}
          role="button"
          aria-label={
            (mp.canDelete ? "Revoke" : "Grant") +
            " permission to delete " +
            mp.moduleName
          }
          onClick={() => toggleCanDelete(mp)}
        >
        { /* We use !! operator to prevent warning about changing an uncontrolled input to be controlled */ }
          <input type="checkbox" checked={!!mp.canDelete} onChange={dummyOnChange} />
        </td>
        <td className={styles["tg-c3ow"]} />
        <td className={styles["tg-c3ow"]} />
        <td className={styles["tg-c3ow"]} />
      </tr>
    )
  );

  const toggleCanView = (modulePermission) => {
    modulePermission.canView = !modulePermission.canView;
    updateModulePermission(modulePermission);
  };

  const toggleCanInsert = (modulePermission) => {
    modulePermission.canInsert = !modulePermission.canInsert;
    updateModulePermission(modulePermission);
  };

  const toggleCanEdit = (modulePermission) => {
    modulePermission.canEdit = !modulePermission.canEdit;
    updateModulePermission(modulePermission);
  };

  const toggleCanDelete = (modulePermission) => {
    modulePermission.canDelete = !modulePermission.canDelete;
    updateModulePermission(modulePermission);
  };

  const updateModulePermission = (modulePermission) => {
    const oldModulePermissions = (role || {}).modulePermissions || [];
    const newModulePermissions = [
      ...oldModulePermissions.filter(
        (mp) => mp.module != modulePermission.module
      ),
      modulePermission,
    ];
    const newRole = { ...role, modulePermissions: newModulePermissions };
    setRole(newRole);
  };

  return (
    <React.Fragment>
      <Alert />
      {isPosRoleCancelDialogVisible && (
        <CancelPosRoleDialog
          onCancel={() => setIsPosRoleCancelDialogVisible(false)}
          onOk={redirectToPosRoleList}
        />
      )}

      {hasNameValidationError && (
        <span className={styles.validationMessage}>&gt;</span>
      )}
      <input
        aria-label="POS role name"
        ref={nameInputRef}
        placehoder="Name"
        className={`${styles.textBoxName} ${
          hasNameValidationError && styles.validationError
        }`}
        value={role.name ?? ""}
        onChange={(event) => setName(event.target.value)}
      />
      <table className={styles.tg}>
        <tbody>
          <tr>
            <th className={styles["tg-fymr"]}>Module</th>
            <th className={styles["tg-7btt"]}>View</th>
            <th className={styles["tg-7btt"]}>Create</th>
            <th className={styles["tg-7btt"]}>Edit</th>
            <th className={styles["tg-7btt"]}>Delete</th>
            <th className={styles["tg-7btt"]}>Search</th>
            <th className={styles["tg-7btt"]}>Override</th>
            <th className={styles["tg-7btt"]}>Void</th>
          </tr>
          {moduleList}
        </tbody>
      </table>

      <div className="col-md-12 section-spacing">
        <div className={`button-wrap-alignment ${styles.buttonContainer}`}>
          {!role ? null : (
            <button
              aria-label="Save role"
              className={`btn button-width ${styles.success} col-md-2 col-md-offset-7`}
              onClick={() => {
                saveRole();
              }}
              disabled={!isSaveEnabled}
            >
              Save
            </button>
          )}
          <button
            aria-label="cancel pos role changes"
            className="btn button-width col-md-2"
            onClick={() => cancelRoleChanges()}
          >
            Cancel
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}
